import { useLocation } from 'preact-iso';
import { useLayoutEffect, useState } from 'preact/hooks';
import waitForTimers from 'wait-for-timers';
import { restorePurchase } from '../../global-state/audio-player';
import { showModal, useSessionStore } from '../../global-state/session';
import { useAdSkipsLeft, useSubscriptionStatus } from '../../global-state/usage-data';
import { useLastSignIn, useUser, useUserActions } from '../../global-state/user';
import { getCommitHash } from '../../utils/debug';
import { classNames } from '../../utils/helpers';
import { isIOSNativeApp } from '../../utils/platform';
import { Icon } from '../icon';
import { Link } from '../link';
import style from './style.module.scss';

export default function TopNav({ isTransparent = false }) {
  const isInApp = useSessionStore((s) => s.isInApp);
  const { route } = useLocation();

  const [isOpen, setOpen] = useState(false);

  // For very first render, use logged-out state to avoid hydration mismatch issues
  const [isFirstRender, setIsFirstRender] = useState(true);
  useLayoutEffect(() => setIsFirstRender(false), []);
  const _user = useUser();
  const user = isFirstRender ? null : _user;

  const lastSignIn = useLastSignIn();
  const { total: totalAdSkipsLeft, description: adSkipText } = useAdSkipsLeft();
  const subscriptionStatus = useSubscriptionStatus();

  const { signOut } = useUserActions();
  const navTransparenceyStyles = isTransparent
    ? {
        background: 'transparent',
        borderColor: 'transparent',
      }
    : {
        boxShadow: '0 0 7px #e7e7e7',
      };
  const buttonTextAndAction: [any, (event: any) => void] =
    user && isInApp
      ? [
          <>
            <span class="hidden min-[395px]:block">Feedback</span>
            <Icon class="h-[20px] w-[20px] min-[395px]:ml-[-4px]" sprite="feedback" />
          </>,
          () => showModal('feedback'),
        ]
      : user
        ? ['Open App', () => route('/now')]
        : lastSignIn
          ? ['Sign In', () => route('/sign-in')]
          : ['Sign Up', () => route('/sign-up')];

  useLayoutEffect(() => {
    if (isOpen) {
      useSessionStore.setState({ isPageBehindVeil: true });
    } else {
      // Wait until it fully closes before marking as hidden
      return waitForTimers([250], () => {
        useSessionStore.setState({ isPageBehindVeil: false });
      });
    }
  }, [isOpen]);

  return (
    <>
      <nav class={style.nav} style={{ ...navTransparenceyStyles }}>
        <Link class="mr-auto flex items-center justify-center" href="/">
          <span
            class={`${style.logoCircle} -mt-1 mr-1`}
            style={isTransparent ? { background: 'transparent' } : {}}
          >
            <Icon
              style={{
                height: isTransparent ? '20px' : '18px',
                width: isTransparent ? '20px' : '18px',
              }}
              sprite="headphones"
            />
          </span>
          <h3 class="m-0 mt-[3px] whitespace-nowrap font-logotext text-[22px]">
            Adblock Podcast
          </h3>
        </Link>
        <button
          class="button button--transparent"
          onClick={buttonTextAndAction[1]}
          style={{
            marginRight: user && user.pictureUrl ? '8px' : '6px',
            '--button-height': '28px',
            padding: '0 6px',
          }}
        >
          {buttonTextAndAction[0]}
        </button>
        <button
          onClick={() => setOpen(!isOpen)}
          class={classNames(
            'color-inherit z-[13] flex h-9 items-center justify-center rounded border-0 bg-transparent',
            user ? 'w-16 justify-end' : 'w-9'
          )}
          aria-label={!isOpen ? 'Open Menu' : 'Close Menu'}
        >
          {!isOpen && user && <AdSkipsIndicator />}
          {user && !isOpen ? (
            <span
              class={classNames(
                'relative h-7 w-7 shrink-0 rounded-full bg-icon-blue text-center text-[16px] leading-7 text-white'
              )}
            >
              {(getName()[0] || getEmail()[0] || '?').toUpperCase()}
              {user.pictureUrl && (
                <span
                  className={classNames(
                    'absolute left-0 top-0 h-full w-full rounded-full',
                    'bg-cover bg-no-repeat'
                  )}
                  style={{
                    backgroundImage: `url(${user.pictureUrl})`,
                  }}
                ></span>
              )}
            </span>
          ) : (
            <span
              class={classNames(
                style.hamburger,
                isOpen ? style.hamburgerToX : '',
                user && user.pictureUrl ? '[&>span]:!transition-none' : ''
              )}
            >
              <span></span>
              <span></span>
              <span></span>
            </span>
          )}
        </button>
        <div class={classNames(style.popupMenu, isOpen ? '' : style.closed)}>
          {user ? (
            <>
              <span>Hi {getName() || 'there'}!</span>
              <span
                class="relative float-left mb-8 mr-3 h-8 w-8 shrink-0 rounded-full border-0 bg-icon-blue text-center text-[18px] leading-8 text-white"
                style={{
                  backgroundImage: user.pictureUrl ? `url(${user.pictureUrl})` : '',
                }}
              >
                {(getName()[0] || getEmail()[0] || '?').toUpperCase()}
                {user.pictureUrl && (
                  <span
                    className={classNames(
                      'absolute left-0 top-0 h-full w-full rounded-full',
                      'bg-cover bg-no-repeat'
                    )}
                    style={{
                      backgroundImage: `url(${user.pictureUrl})`,
                    }}
                  ></span>
                )}
              </span>
              <div className="flex gap-1">
                <Link
                  class={style.popupMenuItem}
                  href="/account"
                  style="text-decoration: underline; cursor: pointer"
                >
                  Account
                </Link>
                •
                <Link
                  class={style.popupMenuItem}
                  onClick={signOut}
                  style="text-decoration: underline; cursor: pointer"
                >
                  Sign Out
                </Link>
              </div>
              <small class="clear-both mb-4 block">
                {adSkipText}{' '}
                {totalAdSkipsLeft < 10 &&
                  (subscriptionStatus === 'active' ? (
                    <>
                      If you're running low, you can{' '}
                      <Link
                        class="text-blue-500 underline"
                        href={isInApp ? '/pricing-in-app' : '/pricing'}
                      >
                        get 100 non-expiring ad skips here!
                      </Link>
                    </>
                  ) : (
                    <>
                      <Link
                        class="text-blue-500 underline"
                        href={isInApp ? '/pricing-in-app' : '/pricing'}
                      >
                        Sign up for a plan to get 50 more ad-skips
                      </Link>{' '}
                      and support podcast creators!
                    </>
                  ))}
              </small>
            </>
          ) : (
            <>
              <Link
                class={style.popupMenuItem}
                style={{ display: 'inline-block', width: '' }}
                href="/sign-in"
              >
                Sign In
              </Link>
              &ensp;/&ensp;
              <Link
                class={style.popupMenuItem}
                style={{ display: 'inline-block', width: '' }}
                href="/sign-up"
              >
                Sign Up
              </Link>
            </>
          )}

          <Link class={style.popupMenuItem} href="/">
            Home
          </Link>
          <Link class={style.popupMenuItem} href="/now">
            Listen Now
          </Link>
          <Link class={style.popupMenuItem} href="/library">
            Library
          </Link>
          <Link class={style.popupMenuItem} href="/search">
            Search
          </Link>
          <Link
            class={style.popupMenuItem}
            href={isInApp ? '/pricing-in-app' : '/pricing'}
          >
            Pricing & FAQ
          </Link>
          {isIOSNativeApp && user && (
            <Link class={style.popupMenuItem} onClick={restorePurchase}>
              Restore Purchase
            </Link>
          )}
          <div
            style={{ display: 'flex', color: '#777', justifyContent: 'space-between' }}
          >
            <small>
              {/* {lastActivationTimestamp
                ? `Updated ${relativeDate(
                    new Date(lastActivationTimestamp)
                  ).toLowerCase()}`
                : ''} */}
            </small>
            <small>
              v1.{getCommitHash()} - {import.meta.env.VITE_BUILD_DATE}
            </small>
          </div>
        </div>
        <div
          class={classNames(style.popupMenuVeil, isOpen ? '' : style.closed)}
          onClick={() => setOpen(!isOpen)}
        />
      </nav>
    </>
  );

  function getName() {
    if (!user || !user.name) return '';
    return user.name.split(' ')[0];
  }

  function getEmail() {
    if (!user || !user.email) return '';
    return user.email;
  }
}

function AdSkipsIndicator() {
  const { total: totalAdSkipsLeft, isLoading: isLoadingAdSkips } = useAdSkipsLeft();
  return (
    <span
      class={classNames(
        style.adSkipsLeftCircle,
        isLoadingAdSkips || totalAdSkipsLeft >= 7
          ? 'bg-green-600'
          : totalAdSkipsLeft > 0
            ? 'bg-yellow-600'
            : 'bg-red-600'
      )}
      style={{
        paddingLeft: totalAdSkipsLeft > 10 ? '2px' : '',
        fontSize: totalAdSkipsLeft < 10 ? '12px' : totalAdSkipsLeft < 100 ? '11px' : '',
        width: totalAdSkipsLeft >= 10 ? '28px' : '',
      }}
    >
      {isLoadingAdSkips ? (
        '...'
      ) : totalAdSkipsLeft > 10 ? (
        <>
          {totalAdSkipsLeft > 100 ? 100 : Math.floor(totalAdSkipsLeft / 10) * 10}
          <span>+</span>
        </>
      ) : (
        totalAdSkipsLeft
      )}
    </span>
  );
}
